import { useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format, parseISO } from "date-fns";
import JobSchedulesSidebar from "./JobSchedulesSidebar";
import { LoaderCircle, Pencil } from "lucide-react";

const JobSchedulesCalendar = ({ jobSchedules, onRefresh }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [jobToEdit, setJobToEdit] = useState(null);
    const [modalJob, setModalJob] = useState(null);
    const [currentView, setCurrentView] = useState("dayGridMonth");

    const handleAddJob = () => {
        setJobToEdit(null);
        setIsAdding(true);
    };

    const handleSidebarSubmit = async () => {
        await onRefresh();
        setIsAdding(false);
        setJobToEdit(null);
    };

    const getEventColor = (schedule) => {
        const now = new Date();
        const startDate = parseISO(schedule.start_time);
        const endDate = parseISO(schedule.end_time);
        if (now < startDate) return "#3b82f6";
        if (now > endDate) return "#6b7280";
        return "#10b981";
    };

    const calendarEvents = useMemo(() => {
        if (!jobSchedules) return [];

        const events = [];

        jobSchedules.forEach((schedule) => {
            const start = parseISO(schedule.start_time);
            const end = parseISO(schedule.end_time);
            const color = getEventColor(schedule);

            if (currentView === "dayGridMonth") {
                events.push({
                    id: schedule.id.toString(),
                    title: `${schedule.device_id} - ${
                        schedule.part?.name || "N/A"
                    }`,
                    start: start.toISOString(),
                    end: end.toISOString(),
                    backgroundColor: color + "1A",
                    borderColor: color,
                    textColor: "#000000",
                    extendedProps: { schedule },
                });
            } else {
                const startDay = new Date(
                    start.getFullYear(),
                    start.getMonth(),
                    start.getDate()
                );
                const endDay = new Date(
                    end.getFullYear(),
                    end.getMonth(),
                    end.getDate()
                );
                const totalDays = Math.floor(
                    (endDay - startDay) / (1000 * 60 * 60 * 24)
                );

                events.push({
                    id: `${schedule.id}-start`,
                    title: `${schedule.device_id} - ${
                        schedule.part?.name || "N/A"
                    }`,
                    start: start.toISOString(),
                    end:
                        totalDays > 0
                            ? new Date(
                                  startDay.getFullYear(),
                                  startDay.getMonth(),
                                  startDay.getDate() + 1
                              ).toISOString()
                            : end.toISOString(),
                    backgroundColor: color + "1A",
                    borderColor: color,
                    textColor: "#000000",
                    extendedProps: { schedule },
                });

                for (let i = 1; i < totalDays; i++) {
                    const midDay = new Date(startDay);
                    midDay.setDate(midDay.getDate() + i);
                    events.push({
                        id: `${schedule.id}-mid-${i}`,
                        title: `[Continues] ${schedule.device_id} - ${
                            schedule.part?.name || "N/A"
                        }`,
                        start: midDay.toISOString(),
                        backgroundColor: color + "1A",
                        allDay: true,
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: { schedule },
                    });
                }

                if (totalDays > 0) {
                    const endStart = new Date(endDay);
                    events.push({
                        id: `${schedule.id}-end`,
                        title: `${schedule.device_id} - ${
                            schedule.part?.name || "N/A"
                        }`,
                        start: endStart.toISOString(),
                        end: end.toISOString(),
                        backgroundColor: color + "1A",
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: { schedule },
                    });
                }
            }
        });

        return events;
    }, [jobSchedules, currentView]);

    const handleDateClick = (dateInfo) => {
        const clickedDate = dateInfo.date;
        const schedulesForDate = jobSchedules.filter((schedule) => {
            const startDate = parseISO(schedule.start_time);
            const endDate = parseISO(schedule.end_time);
            return clickedDate >= startDate && clickedDate <= endDate;
        });
    };

    const handleEventClick = (eventInfo) => {
        const schedule = eventInfo.event.extendedProps.schedule;
        setModalJob(schedule);
    };

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-relative">
            <div className="lg:tw-col-span-3">
                <div className="tw-rounded-lg tw-border tw-bg-white tw-shadow-sm">
                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-px-6 tw-py-4">
                        <h3 className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-flex tw-items-center tw-gap-2">
                            Job Schedules
                        </h3>
                        <button
                            onClick={handleAddJob}
                            className="tw-inline-flex tw-items-center tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-text-sm tw-px-4 tw-py-2 tw-rounded-md tw-shadow-sm tw-transition"
                            disabled={isAdding}
                        >
                            {isAdding ? (
                                <LoaderCircle className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white" />
                            ) : (
                                <>
                                    <svg
                                        className="tw-h-4 tw-w-4 tw-mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                        />
                                    </svg>
                                    Add a Job
                                </>
                            )}
                        </button>
                    </div>
                    <div className="tw-p-6 tw-pt-0">
                        <FullCalendar
                            plugins={[
                                dayGridPlugin,
                                timeGridPlugin,
                                interactionPlugin,
                            ]}
                            initialView="dayGridMonth"
                            viewDidMount={(arg) =>
                                setCurrentView(arg.view.type)
                            }
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,timeGridWeek,timeGridDay",
                            }}
                            events={calendarEvents}
                            dateClick={handleDateClick}
                            eventClick={handleEventClick}
                            height="auto"
                            eventDisplay="block"
                            dayMaxEvents={3}
                            moreLinkClick="day"
                            selectable={true}
                            selectMirror={true}
                            eventAllDayDefault={false}
                            dayHeaderFormat={{ weekday: "short" }}
                            eventTimeFormat={{
                                hour: "numeric",
                                minute: "2-digit",
                                meridiem: "short",
                            }}
                        />
                    </div>
                </div>
            </div>
            {modalJob && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-lg tw-w-full tw-p-6 tw-relative">
                        <button
                            className="tw-absolute tw-top-2 tw-right-2 tw-text-gray-500 hover:tw-text-black"
                            onClick={() => setModalJob(null)}
                        >
                            &times;
                        </button>
                        <h3 className="tw-text-lg tw-font-bold tw-mb-2 tw-flex tw-justify-between tw-items-center">
                            <span>{modalJob.name}</span>
                            <button
                                className="tw-text-blue-600 hover:tw-text-blue-800 tw-ml-2"
                                onClick={() => {
                                    setJobToEdit(modalJob);
                                    setIsAdding(true);
                                    setTimeout(() => {
                                        setModalJob(null);
                                    }, 2000);
                                }}
                                title="Edit Job"
                            >
                                {isAdding && jobToEdit?.id === modalJob.id ? (
                                    <LoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" />
                                ) : (
                                    <Pencil className="tw-h-4 tw-w-4" />
                                )}
                            </button>
                        </h3>

                        <hr className="tw-border tw-border-solid tw-border-black" />

                        <div className="tw-text-sm tw-space-y-1">
                            <div className="tw-flex tw-flex-row tw-row-span-2">
                                <div className="tw-flex-1">
                                    <span className="tw-font-medium">
                                        Part:
                                    </span>{" "}
                                    {modalJob.part?.name}
                                </div>
                                <div className="tw-flex-1">
                                    <span className="tw-font-medium">
                                        Cycle:
                                    </span>{" "}
                                    {modalJob.cycle?.name}
                                </div>
                            </div>
                            <div className="tw-flex tw-flex-row tw-row-span-2">
                                <div className="tw-flex-1">
                                    <span className="tw-font-medium">
                                        From:
                                    </span>{" "}
                                    {format(
                                        parseISO(modalJob.start_time),
                                        "PPpp"
                                    )}
                                </div>
                                <div className="tw-flex-1">
                                    <span className="tw-font-medium">To:</span>{" "}
                                    {format(
                                        parseISO(modalJob.end_time),
                                        "PPpp"
                                    )}
                                </div>
                            </div>
                            {modalJob.description && (
                                <div>
                                    <span className="tw-font-medium">
                                        Description:
                                    </span>{" "}
                                    {modalJob.description}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <JobSchedulesSidebar
                isOpen={isAdding}
                onClose={() => setIsAdding(false)}
                onSubmit={handleSidebarSubmit}
                type={jobToEdit ? "edit" : "add"}
                jobData={jobToEdit}
            />
        </div>
    );
};

export default JobSchedulesCalendar;
